import { NavLink, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  User,
  Settings,
  Bell,
  Building2,
  Link2,
  CreditCard,
  Shield,
  FileText,
  Puzzle,
  Wrench,
  Store,
  Bot,
  Mail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useBillingFeatures } from '../../hooks/useBilling';

interface Tab {
  label: string;
  path: string;
  end?: boolean;
  testId: string;
  icon: LucideIcon;
  /** Feature gate key; the tab is hidden when the account's features lack it (same rule as the nav). */
  featureKey?: string;
}

interface TabGroup {
  label: string;
  tabs: Tab[];
}

const TAB_GROUPS: TabGroup[] = [
  {
    label: 'Account',
    tabs: [
      { label: 'Profile',       path: '/settings/profile',       end: false, testId: 'settings-tab-profile',       icon: User },
      { label: 'General',       path: '/settings',               end: true,  testId: 'settings-tab-general',       icon: Settings },
      { label: 'Notifications', path: '/settings/notifications', end: false, testId: 'settings-tab-notifications', icon: Bell },
    ],
  },
  {
    label: 'Financial',
    tabs: [
      { label: 'ACH Accounts',    path: '/settings/ach-accounts',    end: false, testId: 'settings-tab-ach-accounts',    icon: Building2 },
      { label: 'Linked Accounts', path: '/settings/linked-accounts', end: false, testId: 'settings-tab-linked-accounts', icon: Link2 },
      { label: 'Billing',         path: '/settings/billing',         end: false, testId: 'settings-tab-billing',         icon: CreditCard },
    ],
  },
  {
    label: 'Security & Legal',
    tabs: [
      { label: 'Security', path: '/settings/security', end: false, testId: 'settings-tab-security', icon: Shield },
      { label: 'Legal',    path: '/settings/legal',    end: false, testId: 'settings-tab-legal',    icon: FileText },
    ],
  },
  {
    label: 'Integrations',
    tabs: [
      { label: 'Connected Apps',   path: '/settings/connected-apps',   end: false, testId: 'settings-tab-connected-apps',   icon: Puzzle },
      { label: 'Tool Permissions', path: '/settings/tool-permissions', end: false, testId: 'settings-tab-tool-permissions', icon: Wrench },
      { label: 'Vendors',          path: '/settings/vendors',          end: false, testId: 'settings-tab-vendors',          icon: Store },
    ],
  },
  {
    label: 'Automation',
    tabs: [
      { label: 'AI agents', path: '/settings/agents', end: false, testId: 'settings-tab-agents', icon: Bot },
      { label: 'Agent email', path: '/settings/agent-email', end: false, testId: 'settings-tab-agent-email', icon: Mail, featureKey: 'agentic_support' },
    ],
  },
];


interface SettingsLayoutProps {
  children?: ReactNode;
  /** Hide the phone-width tab strip (a sub-page supplies its own back link instead). */
  hideMobileTabs?: boolean;
}

export function SettingsLayout({ children, hideMobileTabs = false }: SettingsLayoutProps) {
  const { data: featuresData } = useBillingFeatures();
  // Mirrors the nav's feature gate: a keyed tab shows until the loaded features say it is absent.
  const tabGroups = TAB_GROUPS.map((group) => ({
    ...group,
    tabs: group.tabs.filter(
      (tab) => !tab.featureKey || !featuresData || featuresData.features.includes(tab.featureKey),
    ),
  }));
  const tabsFlat = tabGroups.flatMap((g) => g.tabs);

  return (
    <div className="max-w-5xl mx-auto pb-6" data-testid="settings-layout">
      {/* Mobile: horizontal scrollable tab bar */}
      <div className={hideMobileTabs ? 'hidden' : 'md:hidden px-[var(--page-padding-x)] mb-6'}>
        <div
          className="flex gap-1 overflow-x-auto border-b border-border"
          style={{ scrollbarWidth: 'none' }}
        >
          {tabsFlat.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              className={({ isActive }) =>
                [
                  'px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                ].join(' ')
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Shared layout: sidebar on desktop, full-width on mobile */}
      <div className="md:flex md:gap-6">
        {/* Sidebar (desktop only) */}
        <nav
          className="hidden md:block w-48 shrink-0 pl-[var(--page-padding-x)] pt-1"
          aria-label="Settings navigation"
          data-testid="settings-nav"
        >
          <div className="space-y-5">
            {tabGroups.map((group) => (
              <div key={group.label}>
                <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.tabs.map((tab) => (
                    <NavLink
                      key={tab.path}
                      to={tab.path}
                      end={tab.end}
                      data-testid={tab.testId}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                        ].join(' ')
                      }
                    >
                      <tab.icon className="h-4 w-4 shrink-0" />
                      {tab.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Page content */}
        <div className="flex-1 min-w-0">
          {children ?? <Outlet />}
        </div>
      </div>
    </div>
  );
}

export default SettingsLayout;
